// Bell icon: papers a teacher set that are not started yet, then cards due
// across all subjects, sorted by urgency. The count is both together.
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { useTaskCards } from '@/hooks/useTaskCards';
import { useAssignedPapers } from '@/hooks/useAssignedPapers';
import { BIN_FILL } from '@/lib/bins';
import type { TaskCardDTO } from '@/types';

export function NotificationBadge() {
  const { data } = useTaskCards({ due: true });
  const { unstarted: newPapers } = useAssignedPapers();
  const [open, setOpen] = useState(false);

  const due = useMemo<TaskCardDTO[]>(
    () => [...(data ?? [])].sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()),
    [data],
  );
  const count = due.length + newPapers.length;
  const label = [
    newPapers.length > 0 && `${newPapers.length} new paper${newPapers.length === 1 ? '' : 's'}`,
    `${due.length} cards due`,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative rounded-full border border-line bg-card p-1.5 text-ink-soft transition hover:border-forest hover:text-forest"
        aria-label={label}
        aria-expanded={open}
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M6 9a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6Z" strokeLinejoin="round" />
          <path d="M10 20a2 2 0 0 0 4 0" strokeLinecap="round" />
        </svg>
        {count > 0 && (
          <span className="absolute -right-1 -top-1 flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-red-600 px-0.5 text-fs-9 font-bold text-white">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 z-50 mt-2 max-h-96 w-80 overflow-y-auto rounded-2xl border border-slate-200 bg-card p-2 shadow-xl"
          >
            {newPapers.length > 0 && (
              <>
                <p className="px-2 py-1 text-sm font-semibold text-ink">New papers from your teachers</p>
                {newPapers.map((p) => (
                  <Link
                    key={p.id}
                    to="/tests/assigned"
                    onClick={() => setOpen(false)}
                    className="flex items-center gap-2 rounded-lg px-2 py-2 no-underline hover:bg-slate-50"
                  >
                    <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: 'var(--brand-lime)' }} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-slate-700">{p.title}</span>
                      <span className="block truncate text-xs text-slate-400">{p.subjectName}</span>
                    </span>
                    <span className="text-xs font-semibold text-forest">Start →</span>
                  </Link>
                ))}
                <div className="mx-2 my-1 h-px bg-slate-100" />
              </>
            )}
            <p className="px-2 py-1 text-sm font-semibold text-ink">Cards due</p>
            {due.length === 0 && <p className="px-2 py-4 text-sm text-slate-400">Nothing due. Nice!</p>}
            {due.map((c) => {
              const overdue = new Date(c.dueDate).getTime() < Date.now();
              return (
                <div key={c.id} className="flex items-center gap-2 rounded-lg px-2 py-2 hover:bg-slate-50">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: BIN_FILL[c.currentBin] }} />
                  <span className="flex-1 truncate text-sm text-slate-700">{c.topicName}</span>
                  <span className={`text-xs ${overdue ? 'font-semibold text-red-600' : 'text-slate-400'}`}>
                    {overdue ? 'Overdue' : 'Due'}
                  </span>
                </div>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
